import React from "react";
// Consumer Registration Page

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

// Custom
import Layout from "../../layout";
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";

// React-icons
import { MdAssignment } from "react-icons/md";
import { IoHome } from "react-icons/io5";
import { FaArrowRight } from "react-icons/fa";
import { FaCheck } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import { UnifiedIcon } from "../Icon/UnifiedIcon";
import { Icon } from "@mui/material";
import { IconType } from "react-icons";

const ConsumerRegistration: React.FC = () => {
  const steps = [
    {
      title: "Submit Your Interest",
      subTitle: "Fill out the registration form.",
      icon: MdAssignment,
      hasDone: true,
    },
    {
      title: "Site Visit",
      subTitle: "A field  engineer will conduct a site survey.",
      icon: IoHome,
      hasDone: true,
    },
    {
      title: "Approve",
      subTitle: "Survey approval within 7 days.",
      icon: FaCheck,
      hasDone: false,
    },
    {
      title: "Onboarding",
      subTitle: "Receive your credentials via email.",
      icon: MdEmail,
      hasDone: false,
    },
  ];

  const stepsCard = (
    step: number,
    title: string,
    subTitle: string,
    icon: IconType,
    hasDone: boolean
  ) => {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Box
          sx={{
            width: "300px",
            backgroundColor: "#ffffff",
            border: hasDone ? "3px solid #43E97B" : "1px solid #E0E0E0",
            borderRadius: "16px",
            p: 2,
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            position: "relative",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-20px",
              left: "20px",
              backgroundColor: "#0396FF",
              color: "#fff",
              borderRadius: "8px",
              px: 2,
              py: 0.5,
              fontWeight: "bold",
            }}
          >
            Step {step}
          </Box>
          <UnifiedIcon icon={icon} color="#0396FF" fontSize="40px" />
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {subTitle}
          </Typography>

          {steps.length !== step && (
            <Box
              sx={{
                position: "absolute",
                right: "-20px",
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "#0396FF",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <UnifiedIcon
                icon={FaArrowRight}
                color="#ffffff"
                fontSize="22px"
              />
            </Box>
          )}

          {hasDone && (
            <Box
              sx={{
                backgroundColor: "#43E97B",
                color: "#fff",
                borderRadius: "8px",
                px: 2,
                py: 0.5,
                fontWeight: "bold",
                mt: "10px",
              }}
            >
              Completed
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Layout>
      <Card className="meters_title_card">
        <Typography variant="h5">Consumer Registration</Typography>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">How it Works</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            {steps.map((step, index) => {
              return (
                <Grid size={{ xs: 12, md: 3, lg: 3 }}>
                  {stepsCard(
                    index + 1,
                    step.title,
                    step.subTitle,
                    step.icon,
                    step.hasDone
                  )}
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Register Your Interest</Typography>
              <ContainedButton
                label={"Submit your interest"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomInput
                label={"Full Name"}
                placeholder={"Enter your full name"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomInput
                label={"Email Address"}
                placeholder={"Enter your email address"}
                type="email"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomInput
                label={"Phone Number"}
                placeholder={"Enter your phone number"}
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"5px"}>
              <CustomInput
                label={"Address"}
                placeholder={"Enter your address"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"5px"}>
              <CustomOutlinedSelect
                label={"I am interested in participating as a"}
                placeholder={"Prosumer (Energy Producer and Consumer)"}
              />
            </Grid>
          </Grid>

          <Typography
            variant="body1"
            fontSize={"17px"}
            fontWeight={500}
            mt={"30px"}
            mb={"30px"}
          >
            Prosumer Setup Information
          </Typography>

          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <RadioGroup>
                <Typography variant="body1" mb={"5px"}>
                  Do you have an existing solar or energy production setup?
                </Typography>
                <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#0396FF",
                          },
                        }}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#0396FF",
                          },
                        }}
                      />
                    }
                    label="No"
                  />
                </Stack>
              </RadioGroup>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <RadioGroup>
                <Typography variant="body1" mb={"5px"}>
                  Is your setup connected to the grid?
                </Typography>
                <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#0396FF",
                          },
                        }}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#0396FF",
                          },
                        }}
                      />
                    }
                    label="No"
                  />
                </Stack>
              </RadioGroup>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <RadioGroup>
                <Typography variant="body1" mb={"5px"}>
                  Do you have a smart meter installed?
                </Typography>
                <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#0396FF",
                          },
                        }}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        sx={{
                          "&.Mui-checked": {
                            color: "#0396FF",
                          },
                        }}
                      />
                    }
                    label="No"
                  />
                </Stack>
              </RadioGroup>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <CustomInput
                label={
                  "Please provide details about your energy setup (e.g., capacity, type of energy source)"
                }
                placeholder={"Enter details about your energy production setup"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 5, lg: 5 }}>
              <CustomInput
                label={"Additional Comments or Questions (Optional)"}
                placeholder={"Enter any comments or questions here"}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default ConsumerRegistration;
